import { useState } from 'react'
import BaseStep from './BaseStep'
import { useImportContactContext } from './ImportContactContext'
import { FileDragDrop } from 'components/FileDragDrop'
import { Anchor } from 'components/UI/Anchor'
import {
  FileType,
  isValidFileSize,
  readAndParseFileContents,
} from 'utils/fileValidation'

const MAX_ROW_LENGTH = 500

export default function UploadStep() {
  const [fileError, setFileError] = useState<string>()

  const {
    methods: { nextStep, prevStep, setFile, handleClose },
    state: { file },
  } = useImportContactContext()

  /**
   * Checks if a given file is a valid CSV file.
   * @param file The file to be checked.
   * @returns Promise<boolean> indicating if the file is valid or not.
   */
  const isFileValid = async (file: File): Promise<boolean> => {
    setFileError(undefined)
    try {
      // Check file type
      if (file.type !== FileType.CSV) {
        throw new Error('Invalid file type. Please upload a .csv file.')
      }

      // Check file size
      if (!isValidFileSize(file, 1)) {
        throw new Error('File size exceeds 1MB. Please upload a smaller file.')
      }

      // Read and parse file
      const parsedData = await readAndParseFileContents(file)
      console.log(parsedData)

      // Check row count
      if (parsedData.length > MAX_ROW_LENGTH) {
        throw new Error(
          `File size too large. Uploads are limited to ${MAX_ROW_LENGTH} contacts at a time.`
        )
      }

      // Check if there's any blank headers
      if (Object.keys(parsedData[0]).some((it) => it === '')) {
        throw new Error(
          `CSV file contains empty column headers - all columns must have names`
        )
      }

      return true
    } catch (error: any) {
      setFileError(`${error}`)
      return false
    }
  }

  const handleFileChange = async (file: File | null) => {
    if (file) {
      const isValid = await isFileValid(file)
      if (isValid) {
        setFile(file)
        setFileError(undefined)
      }
    } else {
      setFile(null)
      setFileError(undefined)
    }
  }

  return (
    <BaseStep
      handleClose={handleClose}
      onContinue={() => nextStep()}
      onBack={() => prevStep()}
      canContinue={!!file}
      errorText={fileError}
      title="Upload New Contacts - Add File"
      subtitle={
        <>
          First row will be used as column headers. See sample upload{' '}
          <Anchor
            href="https://docs.google.com/spreadsheets/d/1GD9VFtPcQ-4vlxSu3dVWjyLW9SoCinVBZPoCegfjh7I/edit#gid=290823366"
            target="_blank"
          >
            here
          </Anchor>
          .
        </>
      }
    >
      <FileDragDrop
        file={file}
        onFileChange={handleFileChange}
        errorMessage={fileError}
        supportText={`(supports .csv file type up to 1MB each, up to ${MAX_ROW_LENGTH} contacts per file)`}
      />
    </BaseStep>
  )
}
